/* Bronson main product: gallery (more media, mobile carousel, PhotoSwipe lightbox), variant picker, inventory,
   add to bag, accordions, size chart popup, sticky details column and sticky add to bag bar */
if (!customElements.get('bronson-product')) {
  const ICONS = {
    chevron: '<svg aria-hidden="true" class="bmp-pswp-icn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m2 7.5 10 10 10-10"/></svg>',
    close: '<svg aria-hidden="true" class="bmp-pswp-icn" viewBox="0 0 28 28" fill="none" stroke="currentColor" stroke-width="2.6"><path d="m6.5 6.5 15 15M21.5 6.5l-15 15"/></svg>',
    zoom: '<svg aria-hidden="true" class="bmp-pswp-icn" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="10.5" cy="10.5" r="7.5"/><path d="m16 16 6 6M10.5 7v7M7 10.5h7"/></svg>',
  };
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 960px)').matches;

  customElements.define(
    'bronson-product',
    class BronsonProduct extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('[data-bmp-json]');
        this.data = json ? JSON.parse(json.textContent) : { variants: [], images: [] };
        this.cleanup = [];

        this.options = [...this.querySelectorAll('[data-bmp-option]')];
        this.selected = this.options.map((opt) => {
          const btn = opt.querySelector('[data-bmp-value].is-selected') || opt.querySelector('[data-bmp-value]');
          return btn ? btn.dataset.bmpValue : null;
        });
        this.variant = this.findVariant(this.selected) || null;

        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);

        this.initMore();
        this.initCarousel();
        this.initSizeChart();
        this.initAdd();
        this.initSticky();
        this.initStickyMeta();
        this.initSwatches();
        this.rememberView();

        this.update(false);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.cleanup.forEach((fn) => fn());
        this.modal?.remove();
        this.lightbox?.destroy();
        document.documentElement.classList.remove('bmp-lock');
      }

      listen(target, type, fn, opts) {
        target.addEventListener(type, fn, opts);
        this.cleanup.push(() => target.removeEventListener(type, fn, opts));
      }

      /* ---------- clicks ---------- */
      onClick(e) {
        const value = e.target.closest('[data-bmp-value]');
        if (value) {
          const option = value.closest('[data-bmp-option]');
          const index = this.options.indexOf(option);
          if (index > -1) {
            this.selected[index] = value.dataset.bmpValue;
            this.variant = this.findVariant(this.selected);
            this.update(true);
          }
          return;
        }

        const zoom = e.target.closest('[data-bmp-zoom]');
        if (zoom && this.dataset.lightbox === 'true') {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
          e.preventDefault();
          if (this.carouselDragged) return;
          this.openLightbox(Number(zoom.dataset.bmpZoom));
          return;
        }

        const accordion = e.target.closest('[data-bmp-accordion]');
        if (accordion) {
          this.toggleAccordion(accordion);
          return;
        }

        const dot = e.target.closest('[data-bmp-dot]');
        if (dot) {
          this.goTo(Number(dot.dataset.bmpDot));
          return;
        }

        if (e.target.closest('[data-bmp-chart-open]')) {
          this.openModal();
          return;
        }

        if (e.target.closest('[data-bmp-change]')) {
          const picker = this.querySelector('[data-bmp-picker]');
          if (picker) picker.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
          return;
        }

        const sticky = e.target.closest('[data-bmp-add-sticky]');
        if (sticky) this.addToCart(sticky);
      }

      /* ---------- variants ---------- */
      findVariant(selected) {
        return this.data.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      }

      update(fromUser) {
        const variant = this.variant;

        this.options.forEach((opt, index) => {
          opt.querySelectorAll('[data-bmp-value]').forEach((btn) => {
            const value = btn.dataset.bmpValue;
            const isSelected = value === this.selected[index];
            btn.classList.toggle('is-selected', isSelected);
            btn.setAttribute('aria-checked', String(isSelected));
            const combo = [...this.selected];
            combo[index] = value;
            const match = this.findVariant(combo);
            btn.classList.toggle('is-unavailable', !match || !match.available);
          });
          const label = opt.querySelector('[data-bmp-option-value]');
          if (label) label.textContent = this.selected[index] || '';
        });

        const idInput = this.querySelector('[data-bmp-variant-id]');
        if (idInput && variant) idInput.value = variant.id;

        if (variant) {
          this.querySelectorAll('[data-bmp-price]').forEach((el) => {
            el.textContent = variant.price;
            el.classList.toggle('is-sale', Boolean(variant.compare));
          });
          this.querySelectorAll('[data-bmp-compare]').forEach((el) => {
            el.textContent = variant.compare || '';
            el.hidden = !variant.compare;
          });
          const sku = this.querySelector('[data-bmp-sku]');
          if (sku) {
            sku.textContent = variant.sku || '';
            this.querySelector('[data-bmp-sku-wrap]').hidden = !variant.sku;
          }
          const options = this.querySelector('[data-bmp-sticky-options]');
          if (options) options.textContent = variant.options.join(', ');
          const stickyImage = this.querySelector('[data-bmp-sticky-image]');
          if (stickyImage && variant.image && stickyImage.dataset.current !== variant.image) {
            stickyImage.dataset.current = variant.image;
            stickyImage.removeAttribute('srcset');
            stickyImage.src = variant.image;
          }
        }

        this.querySelectorAll('[data-bmp-add]').forEach((btn) => {
          const text = btn.querySelector('[data-bmp-add-text]');
          const available = Boolean(variant && variant.available);
          btn.disabled = !available;
          if (text) text.textContent = !variant ? btn.dataset.unavailableLabel : available ? btn.dataset.addLabel : btn.dataset.soldOutLabel;
        });

        this.updateInventory(variant);

        if (fromUser && variant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
          if (variant.media) this.goToMedia(variant.media);
          this.dispatchEvent(new CustomEvent('bronson:variant-change', { bubbles: true, detail: { variant } }));
        }
      }

      updateInventory(variant) {
        const box = this.querySelector('[data-bmp-inventory]');
        if (!box) return;
        const message = box.querySelector('[data-bmp-inventory-message]');
        const bar = box.querySelector('[data-bmp-inventory-bar]');
        const { low, max, lowText, goodText, outText, unavailableText, untracked } = box.dataset;
        let stock = 'good';
        let text = goodText;
        let percent = 100;

        if (!variant) {
          stock = 'unavailable';
          text = unavailableText;
          percent = 0;
        } else if (!variant.tracked || (variant.policy === 'continue' && variant.inventory <= 0)) {
          if (untracked !== 'true') {
            box.hidden = true;
            return;
          }
        } else if (variant.inventory <= 0) {
          stock = 'empty';
          text = outText;
          percent = 0;
        } else {
          percent = Math.min(100, (variant.inventory / (Number(max) || 40)) * 100);
          if (variant.inventory <= (Number(low) || 5)) {
            stock = 'low';
            text = lowText.replace('[count]', variant.inventory);
          }
        }

        box.hidden = false;
        box.dataset.stock = stock;
        message.textContent = text;
        if (bar) requestAnimationFrame(() => (bar.style.width = `${percent}%`));
      }

      /* ---------- desktop gallery: more media ---------- */
      initMore() {
        this.grid = this.querySelector('[data-bmp-grid]');
        this.moreButton = this.querySelector('[data-bmp-more]');
        if (!this.grid || !this.moreButton) return;
        this.moreButton.addEventListener('click', () => this.toggleMore());
      }

      toggleMore() {
        const grid = this.grid;
        const button = this.moreButton;
        const duration = reduceMotion() ? 0 : parseFloat(getComputedStyle(this).getPropertyValue('--bmp-more-duration')) || 1200;
        clearTimeout(this.moreTimer);

        if (grid.dataset.state === 'closed') {
          grid.dataset.state = 'before';
          const width = grid.getBoundingClientRect().width;
          const full = grid.scrollHeight;
          grid.style.setProperty('--bmp-open', `${width / full}`);
          void grid.offsetWidth;
          grid.dataset.state = 'opening';
          button.textContent = button.dataset.less;
          button.setAttribute('aria-expanded', 'true');
          this.moreTimer = setTimeout(() => {
            grid.dataset.state = 'open';
          }, duration);
        } else {
          const rect = grid.getBoundingClientRect();
          grid.style.transition = 'none';
          grid.style.setProperty('--bmp-open', `${rect.width / rect.height}`);
          grid.dataset.state = 'opening';
          void grid.offsetWidth;
          grid.style.transition = '';
          grid.dataset.state = 'before';
          button.textContent = button.dataset.more;
          button.setAttribute('aria-expanded', 'false');
          this.moreTimer = setTimeout(() => {
            grid.dataset.state = 'closed';
          }, duration);
          if (rect.top < 0) {
            const top = rect.top + window.scrollY - (parseFloat(getComputedStyle(this).getPropertyValue('--bmp-sticky-top')) || 0);
            window.scrollTo({ top, behavior: reduceMotion() ? 'auto' : 'smooth' });
          }
        }
      }

      /* ---------- mobile carousel ---------- */
      initCarousel() {
        const carousel = this.querySelector('[data-bmp-carousel]');
        const viewport = carousel?.querySelector('[data-bmp-viewport]');
        if (!carousel || !viewport) return;
        this.carousel = carousel;
        this.viewport = viewport;
        this.slides = [...carousel.querySelectorAll('[data-bmp-slide]:not(.swiper-slide-duplicate)')];
        this.count = this.slides.length;
        this.dots = [...carousel.querySelectorAll('[data-bmp-dot]')];
        this.dotsInner = carousel.querySelector('[data-bmp-dots-inner]');
        this.index = 0;
        this.dotStart = 0;
        this.updateDots();

        const bind = (swiper) => {
          this.swiper = swiper;
          swiper.on('slideChange', () => {
            this.index = swiper.realIndex;
            this.updateDots();
          });
          // a drag must not open the lightbox
          swiper.on('touchMove', () => {
            this.carouselDragged = true;
          });
          swiper.on('touchEnd', () => setTimeout(() => (this.carouselDragged = false), 60));
        };
        if (viewport.swiper) bind(viewport.swiper);
        else this.listen(viewport, 'carousel:ready', (e) => bind(e.detail));
      }

      goTo(index) {
        if (!this.swiper) return;
        if (this.swiper.params.loop) this.swiper.slideToLoop(index);
        else this.swiper.slideTo(index);
      }

      goToMedia(mediaId) {
        if (!this.slides) return;
        const index = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
        if (index > -1 && index !== this.index) this.goTo(index);
      }


      updateDots() {
        if (!this.dots || !this.dots.length || !this.dotsInner) return;
        const total = this.dots.length;
        const main = 7;
        const size = 18;
        const width = 198;
        let start = 0;
        if (total > main) {
          start = this.dotStart;
          if (this.index < start) start = this.index;
          if (this.index > start + main - 1) start = this.index - main + 1;
          start = Math.max(0, Math.min(total - main, start));
          this.dotStart = start;
        }
        this.dots.forEach((dot, i) => {
          dot.classList.remove('is-main', 'is-near', 'is-active');
          if (total <= main || (i >= start && i < start + main)) dot.classList.add('is-main');
          else if (i === start - 1 || i === start + main) dot.classList.add('is-near');
          if (i === this.index) dot.classList.add('is-active');
          dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
        });
        const offset = total <= main ? (width - total * size) / 2 : width / 2 - ((start + 3) * size + size / 2);
        this.dotsInner.style.transform = `translateX(${offset}px)`;
      }

      /* ---------- lightbox ---------- */
      async openLightbox(index) {
        const images = this.data.images || [];
        if (!images.length) return;
        if (!this.lightbox) {
          try {
            const [lightboxModule, coreModule] = await Promise.all([import(this.dataset.pswpLightbox), import(this.dataset.pswpCore)]);
            const PhotoSwipeLightbox = lightboxModule.default;
            this.lightbox = new PhotoSwipeLightbox({
              dataSource: images.map((img) => ({ src: img.src, msrc: img.msrc, width: img.width, height: img.height, alt: img.alt })),
              pswpModule: coreModule.default,
              mainClass: 'pswp--bmp',
              bgOpacity: 1,
              showHideAnimationType: 'zoom',
              arrowPrevSVG: ICONS.chevron,
              arrowNextSVG: ICONS.chevron,
              closeSVG: ICONS.close,
              zoomSVG: ICONS.zoom,
              imageClickAction: 'zoom',
              tapAction: 'toggle-controls',
              doubleTapAction: 'zoom',
              paddingFn: () => ({ top: 0, bottom: 0, left: 0, right: 0 }),
            });
            this.lightbox.addFilter('thumbEl', (thumbEl, data, i) => this.thumbFor(i) || thumbEl);
            this.lightbox.addFilter('placeholderSrc', (src, slide) => slide.data.msrc || src);
            this.lightbox.on('change', () => {
              const pswp = this.lightbox.pswp;
              if (!pswp || isDesktop()) return;
              const media = images[pswp.currIndex];
              if (media) this.goToMedia(media.id);
            });
            this.lightbox.init();
          } catch (err) {
            const link = this.querySelectorAll('[data-bmp-zoom]')[index];
            if (link) window.open(link.href, '_blank');
            return;
          }
        }
        this.lightbox.loadAndOpen(index);
      }

      thumbFor(index) {
        const image = (this.data.images || [])[index];
        if (!image) return null;
        const scope = isDesktop() ? this.querySelector('.bmp-gallery') : this.querySelector('[data-bmp-track]');
        if (!scope) return null;
        const holder = isDesktop()
          ? scope.querySelector(`.bmp-gallery__item[data-media-id="${image.id}"]`)
          : scope.querySelector(`[data-bmp-slide][data-media-id="${image.id}"]`);
        if (!holder || holder.offsetParent === null) return null;
        return holder.querySelector('img');
      }

      /* ---------- accordions ---------- */
      toggleAccordion(button) {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        panel.getAnimations?.().forEach((a) => a.cancel());
        if (reduceMotion() || !panel.animate) {
          panel.hidden = !open;
          return;
        }
        const timing = { duration: 400, easing: 'cubic-bezier(0.19, 1, 0.22, 1)' };
        if (open) {
          panel.hidden = false;
          panel.animate([{ height: '0px' }, { height: `${panel.scrollHeight}px` }], timing);
        } else {
          const anim = panel.animate([{ height: `${panel.scrollHeight}px` }, { height: '0px' }], timing);
          anim.onfinish = () => {
            if (button.getAttribute('aria-expanded') === 'false') panel.hidden = true;
          };
        }
      }

      /* ---------- size chart ---------- */
      initSizeChart() {
        const modal = this.querySelector('[data-bmp-modal]');
        if (!modal) return;
        modal.setAttribute('style', this.getAttribute('style') || '');
        document.body.appendChild(modal);
        this.modal = modal;
        this.listen(modal, 'click', (e) => {
          if (e.target.closest('[data-bmp-modal-close]')) this.closeModal();
        });
        this.listen(document, 'keydown', (e) => {
          if (e.key === 'Escape' && this.modal?.classList.contains('is-open')) this.closeModal();
        });
      }

      openModal() {
        const modal = this.modal;
        if (!modal) return;
        this.modalTrigger = document.activeElement;
        clearTimeout(this.modalTimer);
        this.loadChartPage(modal.querySelector('[data-bmp-chart-content][data-url]'));
        modal.hidden = false;
        document.documentElement.classList.add('bmp-lock');
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            modal.classList.add('is-open');
            modal.querySelector('.bmp-modal__close')?.focus({ preventScroll: true });
          })
        );
      }

      /* the linked page keeps its chart in a template (sections / apps): load the rendered page */
      async loadChartPage(content) {
        if (!content || content.dataset.loaded) return;
        content.dataset.loaded = 'true';
        content.classList.add('is-loading');
        try {
          const res = await fetch(content.dataset.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const main = doc.querySelector('#MainContent, main');
          if (!main) throw new Error('No main content');
          main.querySelectorAll('script, .shopify-section-group-header-group, .shopify-section-group-footer-group').forEach((el) => el.remove());
          content.innerHTML = main.innerHTML;
        } catch (err) {
          content.innerHTML = `<p><a href="${content.dataset.url}">${content.dataset.url}</a></p>`;
        } finally {
          content.classList.remove('is-loading');
        }
      }

      closeModal() {
        const modal = this.modal;
        if (!modal) return;
        modal.classList.remove('is-open');
        document.documentElement.classList.remove('bmp-lock');
        this.modalTimer = setTimeout(() => {
          if (!modal.classList.contains('is-open')) modal.hidden = true;
        }, 400);
        this.modalTrigger?.focus?.({ preventScroll: true });
      }

      /* ---------- add to bag ---------- */
      initAdd() {
        this.form = this.querySelector('[data-bmp-form]');
        if (!this.form) return;
        this.listen(this.form, 'submit', (e) => {
          e.preventDefault();
          this.addToCart(this.form.querySelector('[data-bmp-add]'));
        });
      }

      async addToCart(button) {
        const variant = this.variant;
        if (!variant || !variant.available || !button || button.classList.contains('is-loading')) return;
        const error = this.querySelector('[data-bmp-error]');
        const body = this.form ? new FormData(this.form) : new FormData();
        body.set('id', variant.id);
        if (!body.has('quantity')) body.set('quantity', 1);
        const bronsonCart = window.BronsonCart;
        if (bronsonCart) {
          body.set('sections', bronsonCart.sections.join(','));
          body.set('sections_url', window.location.pathname);
        }

        this.querySelectorAll('[data-bmp-add]').forEach((b) => b.classList.add('is-loading'));
        if (error) error.hidden = true;
        try {
          const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          const json = await res.json();
          if (!res.ok || json.status) {
            if (error) {
              error.textContent = json.description || json.message || 'Something went wrong. Please try again.';
              error.hidden = false;
            }
            return;
          }
          if (bronsonCart) bronsonCart.added(json, button);
          else window.location.href = window.routes?.cart_url || '/cart';
        } catch (err) {
          if (error) {
            error.textContent = 'Something went wrong. Please try again.';
            error.hidden = false;
          }
        } finally {
          this.querySelectorAll('[data-bmp-add]').forEach((b) => b.classList.remove('is-loading'));
        }
      }

      /* ---------- sticky add to bag bar ---------- */
      initSticky() {
        const bar = this.querySelector('[data-bmp-sticky]');
        const main = this.form?.querySelector('[data-bmp-add]');
        if (!bar || !main || !('IntersectionObserver' in window)) return;
        this.stickyBar = bar;
        let mainVisible = true;
        let footerVisible = false;
        let started = false;
        const sync = () => {
          const hide = mainVisible || footerVisible || !started;
          bar.classList.toggle('is-hidden', hide);
          bar.setAttribute('aria-hidden', String(hide));
          document.documentElement.style.setProperty('--sticky-atc-bar-height', hide ? '0px' : `${bar.offsetHeight}px`);
        };
        const mainObserver = new IntersectionObserver(([entry]) => {
          started = true;
          mainVisible = entry.isIntersecting;
          sync();
        });
        mainObserver.observe(main);
        const footer = document.querySelector('.shopify-section-group-footer-group, footer');
        let footerObserver;
        if (footer) {
          footerObserver = new IntersectionObserver(([entry]) => {
            footerVisible = entry.isIntersecting;
            sync();
          });
          footerObserver.observe(footer);
        }
        this.cleanup.push(() => {
          mainObserver.disconnect();
          footerObserver?.disconnect();
          document.documentElement.style.setProperty('--sticky-atc-bar-height', '0px');
        });
      }

      /* ---------- sticky details column (scrolls with long content) ---------- */
      initStickyMeta() {
        const meta = this.querySelector('[data-bmp-meta]');
        if (!meta || !this.classList.contains('bmp--sticky')) return;
        let lastY = window.scrollY;
        let top = null;
        const onScroll = () => {
          if (!isDesktop()) {
            meta.style.top = '';
            top = null;
            lastY = window.scrollY;
            return;
          }
          const offset = parseFloat(getComputedStyle(this).getPropertyValue('--bmp-sticky-top')) || 0;
          const available = window.innerHeight - offset;
          const height = meta.offsetHeight;
          const dy = window.scrollY - lastY;
          lastY = window.scrollY;
          if (height <= available) {
            top = offset;
          } else {
            const min = window.innerHeight - height - 40;
            top = Math.max(min, Math.min(offset, (top ?? offset) - dy));
          }
          meta.style.top = `${top}px`;
        };
        this.listen(window, 'scroll', onScroll, { passive: true });
        this.listen(window, 'resize', onScroll);
        onScroll();
      }

      /* ---------- product card swatches (wear it with) ---------- */
      initSwatches() {
        this.querySelectorAll('.bmp-featured [data-swatches]').forEach((wrap) => {
          const list = wrap.querySelector('[data-swatch-list]');
          const prev = wrap.querySelector('[data-swatch-prev]');
          const next = wrap.querySelector('[data-swatch-next]');
          if (!list) return;
          const check = () => {
            list.style.maxWidth = '';
            const first = list.querySelector('.bfc-card__swatch');
            if (first && list.scrollWidth > list.clientWidth + 1) {
              const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
              const size = first.offsetWidth;
              const nav = (prev?.offsetWidth || 16) + (next?.offsetWidth || 16);
              const count = Math.max(1, Math.floor((wrap.clientWidth - nav + gap) / (size + gap)));
              list.style.maxWidth = `${count * size + (count - 1) * gap + 2}px`;
            }
            const overflow = list.scrollWidth > list.clientWidth + 1;
            if (prev) {
              prev.hidden = !overflow;
              prev.disabled = list.scrollLeft <= 1;
            }
            if (next) {
              next.hidden = !overflow;
              next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
            }
          };
          list.addEventListener('scroll', check, { passive: true });
          prev?.addEventListener('click', () => list.scrollBy({ left: -48 }));
          next?.addEventListener('click', () => list.scrollBy({ left: 48 }));
          const ro = new ResizeObserver(check);
          ro.observe(list);
          this.cleanup.push(() => ro.disconnect());

          const img = wrap.closest('[data-card]')?.querySelector('[data-primary-image]');
          if (!img) return;
          const original = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
          wrap.querySelectorAll('[data-image]').forEach((swatch) =>
            swatch.addEventListener('mouseenter', () => {
              img.removeAttribute('srcset');
              img.src = swatch.dataset.image;
            })
          );
          wrap.addEventListener('mouseleave', () => {
            img.src = original.src;
            if (original.srcset) img.setAttribute('srcset', original.srcset);
          });
        });
      }

      /* ---------- recently viewed ---------- */
      rememberView() {
        const handle = this.dataset.handle;
        if (!handle) return;
        try {
          const key = 'bronson:recently-viewed';
          const list = JSON.parse(window.localStorage.getItem(key) || '[]').filter((h) => h !== handle);
          list.unshift(handle);
          window.localStorage.setItem(key, JSON.stringify(list.slice(0, 20)));
        } catch (err) {}
      }
    }
  );
}
